import React, { useEffect,useState } from "react";
import { useParams } from "react-router-dom";
import useResumeStore from "../../store/resume.store";
import useJDStore from "../../store/jd.store";
import {Input, Card, Button, Form, Progress, Tag} from "antd"
import { RobotOutlined } from "@ant-design/icons";
import ResumeSkillsCardTable from "../ResumeSkillsCardTable";
import StackChart from "../StackChart";

import "./resumeAnalysisNew.css";

function ResumeAnalysisNew( {jdID,rID}) {

  const { value } = useParams();
  const { allApplications } = useResumeStore();
  const { selectedJD } = useJDStore();

  const [checkAgain] = useState(false);
  const [canDetails, setCandidateDetails] = useState(null);

  const useAIKeywords = selectedJD?.useaigenkeywords === true;
  const titleList = [
    "Keyword",
    ...(useAIKeywords ? ["✦ Skill Group", "✦ Classification"] : []),
    "Suitability",
    "Found / Required Occurance"
  ];
  const roleTitleList = ["Keyword", "Suitability", "Found / Required Occurance"];
  const jdSkillGroups = selectedJD?.jdaiskillsgroup || {};
  const finalTitleList = ["Analysis Type", "Matching %", "Suitability"];

  useEffect(() => {
    const application = allApplications?.find(app => app.qhrid === rID);
    if (!application) {
      setCandidateDetails(null);
      return;
    }

    setCandidateDetails({
      name: application.applicationid?.name || "",
      email: application.applicationid?.email || "",
      mobile: application.applicationid?.mobile || "",
      role: application.resumescore?.role,
      finalScore: application.resumescore || {},
    });
  }, [rID, value, allApplications]);

  const candidateFound = Boolean(canDetails);
  const finalScore = canDetails?.finalScore;
  const skillScores = finalScore?.scorelist?.skill_scores;
  const roleScores = finalScore?.scorelist?.role_scores;

  const getMappedScore = (score) => {
    if (score == null) return "--";
    if (score < 30) return "Not Suitable";
    if (score < 40) return "Below Average";
    if (score < 50) return "Average";
    if (score < 60) return "Above Average";
    return "Good Match";
  };

  const getPartialSuitability = (score, partialScore) => {
    if (score == null) return "--";
    if (score >= 30) return "--";
    return partialScore >= 60 ? "Partial Suitable" : "Not Suitable";
  };

  const getFinalVerdict = () => {
    if (!finalScore) return "--";
    if (finalScore.eduverdict?.verdict !== "Suitable" ||
        finalScore.expverdict?.verdict !== "Suitable") {
      return "Not Suitable";
    }
    return finalScore.scorestr || "--";
  };

  const getKeywordsData = (scoreItem, showOptional = false) => {
    const keywords = scoreItem?.keywords || {};
    const order = scoreItem?.keywordOrder?.length
      ? scoreItem.keywordOrder
      : Object.keys(keywords);

    return order
      .filter(skill => keywords[skill] != null)
      .map(skill => {
        const score = keywords[skill];
        const group = jdSkillGroups[skill] || {};
        return {
          skill: showOptional && !score.ismandatory
            ? `${skill} (optional) `
            : skill,
          ...(showOptional && useAIKeywords ? {
            skillgroup: group.skillgroup || "--",
            skillclassification: group.skillclassification || "--",
          } : {}),
          avg: score.avg == null || score.avg === "" || score.avg === 0
            ? "Not Suitable"
            : `${score.avg}%`,
          total: `${score.found || 0} / ${score.total ?? ""}`,
        };
      });
  };

  const verdict = getFinalVerdict();
  const displayScore = verdict === "Partial Suitable" ? finalScore?.partialscore : finalScore?.score;
  const validPercent = value => typeof value === "number" && Number.isFinite(value);
  const LIGHT_GREEN = "#86efac";
  const LIGHT_RED = "#fca5a5";
  const getProgressColor = score => validPercent(score) && score >= 30 ? LIGHT_GREEN : LIGHT_RED;
  const getVerdictColor = value => value?.trim().toLowerCase() === "suitable" ? LIGHT_GREEN : LIGHT_RED;
  const verdictColor = verdict === "Good Match" || verdict === "Suitable" ? "green"
    : verdict === "Not Suitable" ? "red" : "blue";

  const dashboardMetrics = [
    { label: "Skill Match", value: skillScores?.score, progress: true },
    { label: "Role Match", value: roleScores?.score, progress: true },
    { label: "Mandatory Skills", value: skillScores?.score4, progress: true },
    { label: "Partial Match", value: skillScores?.score3, progress: true },
    { label: "Experience", value: finalScore?.expverdict?.verdict, color: getVerdictColor(finalScore?.expverdict?.verdict) },
    { label: "Education", value: finalScore?.eduverdict?.verdict, color: getVerdictColor(finalScore?.eduverdict?.verdict) },
  ];

  const dashboardSkills = (skillScores?.keywordOrder?.length
    ? skillScores.keywordOrder
    : Object.keys(skillScores?.keywords || {}))
    .filter(skill => skillScores?.keywords?.[skill] != null);

  // Placeholder retained: the original form did not implement re-analysis.
  const handleAnalyzeAgain = () => {};

  return (
   <div className="resumeAnalysisNew-wrapper">
      {candidateFound == true?
      <div className="resumeAnalysisNew-verdicts">
        {/* Comparison dashboard: original Final Analysis table remains below. */}
        <div className="resumeAnalysisNew-verdicts-skills">
          <div className="resumeAnalysisNew-verdicts-others-items">
            <Card bordered={true} style={{ borderRadius: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1, color: "#64748b" }}>
                    CANDIDATE SUITABILITY
                  </div>
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{verdict}</span>
                    <Tag color={verdictColor}>{verdict}</Tag>
                    {useAIKeywords && <Tag color="blue"><RobotOutlined /> AI Enhanced</Tag>}
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <Progress
                    type="circle"
                    size={92}
                    percent={validPercent(displayScore) ? Math.max(0, Math.min(100, displayScore)) : 0}
                    format={() => validPercent(displayScore) ? `${displayScore}%` : "--"}
                    strokeColor={getProgressColor(displayScore)}
                  />
                  <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>Weighted Score -  {finalScore?.scorestr || "--"}</div>
                </div>
              </div>
              {/* Candidate information inside the comparison dashboard. */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, padding: "14px 16px", marginBottom: 16, background: "#f8fafc", border: "1px solid #e8edf3", borderRadius: 9 }}>
                {[
                  { label: "Resume ID", value: rID },
                  { label: "Candidate Name", value: canDetails?.name },
                  { label: "Email", value: canDetails?.email },
                  { label: "Mobile", value: canDetails?.mobile },
                ].map(item => (
                  <div key={item.label} style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 5 }}>{item.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", overflowWrap: "anywhere" }}>{item.value || "--"}</div>
                  </div>
                ))}
              </div>
              {dashboardSkills.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>JD SKILLS</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {dashboardSkills.map(skill => {
                      const matched = Number(skillScores?.keywords?.[skill]?.found) > 0;
                      return (
                        <Tag key={skill} color={matched ? "blue" : "default"} style={{ margin: 0, padding: "3px 9px", borderRadius: 6 }}>
                          {skill}
                        </Tag>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>Blue: found in resume · Gray: not found</div>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
                {dashboardMetrics.map(metric => (
                  <div key={metric.label} style={{ padding: 14, border: `1px solid ${metric.progress ? "#e8edf3" : metric.color}`, borderRadius: 9, background: metric.progress ? "#fafcff" : metric.color === LIGHT_GREEN ? "#f0fdf4" : "#fef2f2" }}>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>{metric.label}</div>
                    <div style={{ fontSize: metric.progress ? 21 : 15, fontWeight: 650, color: metric.progress ? "#0f172a" : metric.color === LIGHT_GREEN ? "#15803d" : "#dc2626" }}>
                      {metric.progress
                        ? (validPercent(metric.value) ? `${metric.value}%` : "--")
                        : (metric.value || "--")}
                    </div>
                    {metric.progress && (
                      <Progress
                        percent={validPercent(metric.value) ? Math.max(0, Math.min(100, metric.value)) : 0}
                        showInfo={false}
                        size="small"
                        strokeColor={getProgressColor(metric.value)}
                        style={{ marginTop: 5 }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
       
        <div className = "resumeAnalysisNew-verdicts-skills">
          <div className="resumeAnalysisNew-verdicts-others-items">
              <Card bordered={true}>
                 {
                   Boolean(finalScore) ?
                       (<div>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                          <b>Skill Analysis</b>
                          {useAIKeywords && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#E6F4FF", color: "#1677FF", padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                              <RobotOutlined /> AI Enhanced
                            </span>
                          )}
                        </span>
                            {finalScore?.scorestr ==="Partial Suitable"?
                             skillScores?.score3
                            :skillScores?.score}% Matched. <br></br>
                            Mandatory: {skillScores?.score4} % 
                            Optional: {skillScores?.score2} % 
                            Partial: {skillScores?.score3 != undefined?skillScores?.score3:"--"} %
                            <br></br>
                          <div>
                              <ResumeSkillsCardTable
                              titleList={titleList}
                              data={getKeywordsData(skillScores, true)}
                              shortTable = {useAIKeywords?false:true }
                              />
                          </div>                          
                        </div>):""
                 }
              </Card>
          </div>
        </div>
        <div className = "resumeAnalysisNew-verdicts-skills">
          <div className="resumeAnalysisNew-verdicts-others-items">
              <Card bordered={true}>
                 {
                   Boolean(finalScore) ?
                       (<div>
                         <b>Role Analysis</b> - Candidate is {roleScores?.score} % suitable for the {canDetails?.role} role.
                          <div>
                              <ResumeSkillsCardTable
                              titleList={roleTitleList}
                              data={getKeywordsData(roleScores)}
                              shortTable
                              />
                          </div>                          
                        </div>):""
                 }
              </Card>
          </div>
        </div>  
        <div className = "resumeAnalysisNew-verdicts-skills">
          <div className="resumeAnalysisNew-verdicts-others-items">
              <Card  bordered={true}>
                 {
                   Boolean(finalScore) ?
                       (<div>
                          <b>Education Analysis</b> - {finalScore?.eduverdict?.verdict } - {finalScore?.eduverdict?.description}<br></br>
                          <br></br>
                          {   
                              finalScore?.edudetailstype =="Normal"?
                              finalScore?.edudetails?.map((row, idx) => {
                                return (
                                <div key={idx}>
                                  {row}
                                </div>)  
                               }):
                               finalScore?.edudetailstable?.map((row, idx) => {
                                return (
                                <div key={idx}>
                                  {row.map((item,i)=>{
                                      return (
                                        <div key={i}>
                                           {item}
                                        </div>
                                      )
                                  })}
                                </div>)  
                               })
                        }              
                        </div>):""
                 }
              </Card>
          </div>
        </div>    
        <div className = "resumeAnalysisNew-verdicts-skills">
          <div className="resumeAnalysisNew-verdicts-others-items">
            <Card bordered={true}>
                  <b>Experience Analysis</b> - {finalScore?.expverdict?.verdict} <br></br><br></br>
                  Total Exp {finalScore?.cumulativeexp?.[0]?.c_yrs_months} Yrs <br></br>
                  Expected Exp {finalScore?.cumulativeexp?.[0]?.e_yrs_months} Yrs
            </Card>
          </div>
        </div>                     
        <div className = "resumeAnalysisNew-verdicts-others">        
         <div className="resumeAnalysisNew-verdicts-others-items">
            <Card title="Grammer Analysis" bordered={true}>
              Card content
            </Card>
         </div>          
         <div className="resumeAnalysisNew-verdicts-others-items">
            <Card title="Barred Company Analysis" bordered={true}>
              Card content
            </Card>
         </div> 
         <div className="resumeAnalysisNew-verdicts-others-items">
            <Card title="Barred College Analysis" bordered={true}>
              Card content
            </Card>
         </div>                                                     
     
        </div>
      </div>       
      :<></>
      }
            
   </div>
  );
}

export default ResumeAnalysisNew;
